import { ref } from 'vue'
import {
  sanitizePackCounts,
  type PlayerMailClaimPayload,
  type PlayerMailListPayload,
  type PlayerMailPayload,
  type PlaytimePackMailMeta,
  type EventQueueMailMeta,
  type SeasonCpuMailMeta,
  type TournamentMailMeta,
  isEventQueueMode,
} from '@shared/index'
import { t } from '../i18n/t'
import { api } from '../api/http'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'
import { useWalletStore } from '../stores/wallet'

const OFFERED_KEY = 'dc.mail.offered'
const COIN_MAX = 10_000
const PACK_MAX = 100

export const mailItems = ref<PlayerMailPayload[]>([])
export const mailUnread = ref(0)
export const mailLoaded = ref(false)
export const mailError = ref(false)
export const mailFocusId = ref<string | null>(null)
export const mailClaimBusy = ref(false)
export const mailClaimError = ref(false)
export const mailClearBusy = ref(false)
export const mailClearError = ref(false)

let inflight: Promise<void> | null = null

export function pendingReward(mail: PlayerMailPayload): boolean {
  return (mail.coins > 0 || mail.packs > 0) && mail.claimedAt == null
}

export function isUnread(mail: PlayerMailPayload): boolean {
  if (mail.coins > 0 || mail.packs > 0) return mail.claimedAt == null
  return mail.readAt == null
}

/** A note the player already finished: claimed, or opened with nothing to claim. */
export function isSettled(mail: PlayerMailPayload): boolean {
  if (mail.claimedAt) return true
  return mail.coins === 0 && mail.packs === 0 && mail.readAt != null
}

export function clearPlayerMail(): void {
  mailItems.value = []
  mailUnread.value = 0
  mailLoaded.value = false
  mailError.value = false
  mailFocusId.value = null
  mailClaimBusy.value = false
  mailClaimError.value = false
  mailClearBusy.value = false
  mailClearError.value = false
}

function readOffered(): Set<string> {
  try {
    const raw = sessionStorage.getItem(OFFERED_KEY)
    if (!raw) return new Set()
    return new Set(raw.split(',').filter(Boolean))
  } catch {
    return new Set()
  }
}

function rememberOffered(id: string): void {
  try {
    const next = readOffered()
    next.add(id)
    sessionStorage.setItem(OFFERED_KEY, [...next].slice(-30).join(','))
  } catch {
    /* private mode */
  }
}

function boundedInt(raw: unknown, max: number): number {
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= max
    ? raw
    : 0
}

function seasonCpuMeta(raw: unknown): SeasonCpuMailMeta | null {
  if (!raw || typeof raw !== 'object') return null
  const rec = raw as Record<string, unknown>
  const difficulty = rec.difficulty
  if (difficulty !== 'hard' && difficulty !== 'expert' && difficulty !== 'master') return null
  if (typeof rec.seasonId !== 'string' || rec.seasonId.length < 1) return null
  if (typeof rec.position !== 'number' || !Number.isInteger(rec.position) || rec.position < 1) {
    return null
  }
  if (typeof rec.wins !== 'number' || !Number.isInteger(rec.wins) || rec.wins < 1) return null
  return {
    seasonId: rec.seasonId,
    difficulty,
    position: rec.position,
    wins: rec.wins,
  }
}

function eventQueueMeta(raw: unknown): EventQueueMailMeta | null {
  if (!raw || typeof raw !== 'object') return null
  const rec = raw as Record<string, unknown>
  if (typeof rec.eventId !== 'string' || rec.eventId.length < 1) return null
  if (!isEventQueueMode(rec.mode)) return null
  if (typeof rec.position !== 'number' || !Number.isInteger(rec.position) || rec.position < 1) {
    return null
  }
  if (typeof rec.wins !== 'number' || !Number.isInteger(rec.wins) || rec.wins < 1) return null
  return {
    eventId: rec.eventId,
    mode: rec.mode,
    position: rec.position,
    wins: rec.wins,
  }
}

function tournamentMeta(raw: unknown): TournamentMailMeta | null {
  if (!raw || typeof raw !== 'object') return null
  const place = (raw as { place?: unknown }).place
  if (
    place === 'champion' ||
    place === 'finalist' ||
    place === 'semi' ||
    place === 'quarters' ||
    place === 'eighths' ||
    place === 'play'
  ) {
    return { place }
  }
  return null
}

function playtimeMeta(raw: unknown): PlaytimePackMailMeta | null {
  if (!raw || typeof raw !== 'object') return null
  const position = (raw as { position?: unknown }).position
  if (typeof position !== 'number' || !Number.isInteger(position) || position < 1) return null
  return { position }
}

function sanitizeMail(raw: Partial<PlayerMailPayload> | null | undefined): PlayerMailPayload | null {
  if (!raw || typeof raw.id !== 'string' || typeof raw.body !== 'string') return null
  const coins = boundedInt(raw.coins, COIN_MAX)
  const packs = boundedInt(raw.packs, PACK_MAX)
  const season = raw.kind === 'season_cpu' ? seasonCpuMeta(raw.meta) : null
  const playtimeKind = raw.kind === 'playtime_mrd' || raw.kind === 'playtime_pack'
    ? raw.kind
    : null
  const playtime = playtimeKind ? playtimeMeta(raw.meta) : null
  const tournament = raw.kind === 'tournament' ? tournamentMeta(raw.meta) : null
  const eventQueue = raw.kind === 'event_queue' ? eventQueueMeta(raw.meta) : null
  return {
    id: raw.id,
    body: raw.body,
    coins,
    packs,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
    claimedAt: typeof raw.claimedAt === 'string' ? raw.claimedAt : null,
    readAt: typeof raw.readAt === 'string' ? raw.readAt : null,
    kind: season
      ? 'season_cpu'
      : playtime
        ? playtimeKind
        : tournament
          ? 'tournament'
          : eventQueue
            ? 'event_queue'
            : null,
    meta: season ?? playtime ?? tournament ?? eventQueue,
  }
}

/** Note text in the current language. Staff mail keeps the stored body. */
export function mailMessage(mail: PlayerMailPayload): string {
  if (mail.kind === 'season_cpu' && mail.meta && 'difficulty' in mail.meta) {
    return t('mailbox.seasonCpu', {
      position: mail.meta.position,
      difficulty: t(`settings.vsAiLevels.${mail.meta.difficulty}`),
    })
  }
  if ((mail.kind === 'playtime_mrd' || mail.kind === 'playtime_pack') && mail.meta && !('difficulty' in mail.meta)) {
    return t('mailbox.playtimeMrd', { position: mail.meta.position })
  }
  if (mail.kind === 'tournament' && mail.meta && 'place' in mail.meta && !('position' in mail.meta)) {
    return t(`mailbox.tournament.${mail.meta.place}`, { coins: mail.coins })
  }
  if (mail.kind === 'event_queue' && mail.meta && 'mode' in mail.meta && 'position' in mail.meta) {
    return t('mailbox.eventQueue', {
      position: mail.meta.position,
      mode: t(`eventQueue.modes.${mail.meta.mode}`),
    })
  }
  return mail.body
}

/** Pack line on a mailbox note. Shop rewards stay Master Packs. */
export function mailPackPhrase(mail: PlayerMailPayload): string {
  if (mail.packs < 1) return ''
  if (mail.kind === 'playtime_mrd' || mail.kind === 'playtime_pack') return t('mailbox.playtimePack')
  return mail.packs === 1 ? t('mailbox.pack') : t('mailbox.packs', { n: mail.packs })
}

function offerNewest(): void {
  if (mailFocusId.value) return
  const newest = mailItems.value.find(
    (row) => pendingReward(row) && !readOffered().has(row.id),
  )
  if (newest) mailFocusId.value = newest.id
}

export function focusPlayerMail(id: string): void {
  if (!mailItems.value.some((row) => row.id === id)) return
  mailClaimError.value = false
  mailFocusId.value = id
}

/** Live socket: a new note from the team. */
export function receivePlayerMail(raw: PlayerMailPayload): void {
  const mail = sanitizeMail(raw)
  if (!mail) return
  const exists = mailItems.value.some((row) => row.id === mail.id)
  if (!exists) {
    mailItems.value = [mail, ...mailItems.value]
    if (isUnread(mail)) mailUnread.value += 1
  }
  if (!exists && pendingReward(mail) && !mailFocusId.value && !readOffered().has(mail.id)) {
    mailClaimError.value = false
    mailFocusId.value = mail.id
  }
}

export function pullPlayerMail(): Promise<void> {
  if (inflight) return inflight
  inflight = loadMail().finally(() => {
    inflight = null
  })
  return inflight
}

async function loadMail(): Promise<void> {
  const token = useAuthStore().token
  if (!token) return
  try {
    const body = await api<PlayerMailListPayload>('/account/mail', { token })
    const mail = (Array.isArray(body.mail) ? body.mail : [])
      .map((row) => sanitizeMail(row))
      .filter((row): row is PlayerMailPayload => row != null)
    mailItems.value = mail
    mailUnread.value = Number.isInteger(body.unread) && body.unread > 0 ? body.unread : 0
    mailLoaded.value = true
    mailError.value = false
    if (mailFocusId.value && !mail.some((row) => row.id === mailFocusId.value)) {
      mailFocusId.value = null
    }
    offerNewest()
  } catch {
    mailError.value = true
    mailLoaded.value = true
  }
}

export async function claimPlayerMail(id: string): Promise<void> {
  if (mailClaimBusy.value) return
  const token = useAuthStore().token
  if (!token) return
  mailClaimBusy.value = true
  mailClaimError.value = false
  const collection = useCollectionStore()
  const epoch = collection.mutationEpoch()
  const before = mailItems.value.find((item) => item.id === id)
  const wasUnread = before ? isUnread(before) : false
  try {
    const body = await api<PlayerMailClaimPayload>(
      `/account/mail/${encodeURIComponent(id)}/claim`,
      { method: 'POST', token },
    )
    const row = mailItems.value.find((item) => item.id === id)
    if (row) {
      row.claimedAt = body.claimedAt
      row.readAt = body.claimedAt
    }
    if (wasUnread && mailUnread.value > 0) mailUnread.value -= 1
    if (collection.mutationEpoch() === epoch) {
      if (typeof body.balance === 'number' && Number.isFinite(body.balance)) {
        useWalletStore().setCoins(body.balance)
      }
      collection.applyPacks(sanitizePackCounts(body.packCounts))
    }
    rememberOffered(id)
    if (mailFocusId.value === id) mailFocusId.value = null
  } catch {
    mailClaimError.value = true
  } finally {
    mailClaimBusy.value = false
  }
}

async function markMailRead(id: string): Promise<void> {
  const token = useAuthStore().token
  if (!token) return
  const row = mailItems.value.find((item) => item.id === id)
  if (!row || row.readAt || pendingReward(row)) return
  const wasUnread = isUnread(row)
  try {
    const body = await api<{ readAt: string }>(
      `/account/mail/${encodeURIComponent(id)}/read`,
      { method: 'POST', token },
    )
    row.readAt = typeof body.readAt === 'string' ? body.readAt : new Date().toISOString()
    if (wasUnread && mailUnread.value > 0) mailUnread.value -= 1
  } catch {
    /* The badge stays until the next visit. */
  }
}

/** Removes read notes and claimed rewards. Pending rewards stay. */
export async function clearSettledMail(): Promise<void> {
  if (mailClearBusy.value) return
  const token = useAuthStore().token
  if (!token) return
  mailClearBusy.value = true
  mailClearError.value = false
  try {
    await api('/account/mail/clear', { method: 'POST', token })
    const kept = mailItems.value.filter((row) => !isSettled(row))
    const focused = mailFocusId.value
    if (focused && !kept.some((row) => row.id === focused)) mailFocusId.value = null
    mailItems.value = kept
  } catch {
    mailClearError.value = true
  } finally {
    mailClearBusy.value = false
  }
}

/** Closes the claim dialog. A note with no reward is marked read. */
export function dismissMailDialog(): void {
  const id = mailFocusId.value
  const row = id ? mailItems.value.find((item) => item.id === id) : undefined
  mailFocusId.value = null
  mailClaimError.value = false
  if (!id) return
  rememberOffered(id)
  if (row && !pendingReward(row) && !row.readAt) void markMailRead(id)
}
